/**
 * The boards list.
 *
 * `?conversation=` / `?meeting=` are passed to the server untouched: the answer
 * is the caller's own boards plus that room's, and which of those a member may
 * see is the server's decision, not a filter here.
 *
 * `BoardPage` lives in `Board.tsx` and is re-exported so the routes keep one
 * import for the surface.
 */

import { useCallback, useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { useT } from '../i18n';
import { formatDay } from '../i18n/format';
import { Empty, PanelHeader } from '../components/Shell';
import { ApiError } from '../lib/api';
import { createBoard, deleteBoard, listBoards, renameBoard, type BoardScope } from '../lib/board/api';
import type { Board } from '../lib/board/types';

export { BoardPage } from './Board';
import { BoardPage as OpenBoard } from './Board';

/**
 * The surface: the list of boards, and the one that is open beside it.
 *
 * One pane rather than the shell's list column, because that is the shape the
 * desktop app draws — and because a canvas is the one thing here that wants
 * every pixel it can have. The list is narrow and fixed; the plane takes the
 * rest.
 */
export function BoardsSurface({ id }: { id?: string }) {
  const t = useT();
  return (
    <div className="rl-boards">
      <aside className="rl-boards-list">
        <BoardsList />
      </aside>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {id ? <OpenBoard id={id} /> : <Empty title={t('boards.pick')} />}
      </div>
    </div>
  );
}

export function BoardsList() {
  const { Button, IconButton } = ds();
  const t = useT();
  const { path, query, navigate } = useRouter();

  const conversationId = query.get('conversation') ?? '';
  const meetingId = query.get('meeting') ?? '';

  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const scope: BoardScope = {
    ...(conversationId ? { conversationId } : {}),
    ...(meetingId ? { meetingId } : {})
  };

  const refresh = useCallback(() => {
    setLoading(true);
    listBoards({
      ...(conversationId ? { conversationId } : {}),
      ...(meetingId ? { meetingId } : {})
    })
      .then((rows) => {
        setBoards(rows);
        setProblem('');
      })
      .catch((err: unknown) => setProblem(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [conversationId, meetingId]);

  useEffect(refresh, [refresh]);

  const onNew = () => {
    createBoard(t('boards.untitled'), scope)
      .then((board) => {
        setBoards((rows) => [board, ...rows]);
        navigate(`/boards/${board.id}`);
      })
      .catch((err: unknown) => setProblem(err instanceof ApiError ? err.message : String(err)));
  };

  const onRename = (id: string, title: string) => {
    setRenaming(null);
    const trimmed = title.trim();
    if (!trimmed) return;
    setBoards((rows) => rows.map((b) => (b.id === id ? { ...b, title: trimmed } : b)));
    renameBoard(id, trimmed).catch((err: unknown) => {
      setProblem(err instanceof ApiError ? err.message : String(err));
      refresh();
    });
  };

  const onDelete = (id: string) => {
    setConfirming(null);
    setBoards((rows) => rows.filter((b) => b.id !== id));
    if (path === `/boards/${id}`) navigate('/boards');
    deleteBoard(id).catch((err: unknown) => {
      setProblem(err instanceof ApiError ? err.message : String(err));
      refresh();
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PanelHeader
        title={t('boards.title')}
        actions={<IconButton icon="Plus" label={t('boards.new')} onClick={onNew} />}
      />

      {problem && (
        <div style={{ padding: 'var(--space-8) var(--space-16)', color: 'var(--danger-500)' }}>
          {problem}
        </div>
      )}

      {loading ? (
        <Empty title={t('boards.loading')} />
      ) : boards.length === 0 ? (
        <Empty
          title={t('boards.empty.title')}
          body={t('boards.empty.body')}
          action={<Button onClick={onNew}>{t('boards.new')}</Button>}
        />
      ) : (
        <div className="mv-scroll" style={{ flex: 1, padding: 'var(--space-8) 0' }}>
          {boards.map((board) => (
            <div
              key={board.id}
              className="mv-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-8)',
                padding: 'var(--space-10) var(--space-16)',
                background: path === `/boards/${board.id}` ? 'var(--app-panel-alt)' : undefined
              }}
            >
              {renaming?.id === board.id ? (
                // A plain input rather than the design system's: this one
                // commits on blur and on Enter, and `Input` takes neither.
                <input
                  autoFocus
                  aria-label={t('boards.rename')}
                  value={renaming.title}
                  onChange={(e) => setRenaming({ id: board.id, title: e.target.value })}
                  onBlur={() => onRename(board.id, renaming.title)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onRename(board.id, renaming.title);
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    font: 'inherit',
                    color: 'var(--text-strong)',
                    background: 'var(--surface-sunken)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 10,
                    padding: 'var(--space-6) var(--space-8)'
                  }}
                />
              ) : (
                <Link to={`/boards/${board.id}`} style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{board.title || t('boards.untitled')}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {formatDay(board.updatedAt)}
                  </div>
                </Link>
              )}

              {confirming === board.id ? (
                <>
                  <Button size="sm" variant="danger" onClick={() => onDelete(board.id)}>
                    {t('boards.delete')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                    {t('boards.cancel')}
                  </Button>
                </>
              ) : (
                <>
                  <IconButton
                    icon="Edit"
                    label={t('boards.rename')}
                    size="sm"
                    variant="ghost"
                    onClick={() => setRenaming({ id: board.id, title: board.title })}
                  />
                  <IconButton
                    icon="Trash2"
                    label={t('boards.delete')}
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirming(board.id)}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
